import React, { useMemo } from "react";
import {
    getToastColors,
    normalizeToastVariant,
    resolveToastThemeFromDom,
    ToastTheme,
    ToastVariant,
} from "./toastPalette";

interface NotificationToastProps {
    title: string;
    message: string;
    variant?: ToastVariant | string;
    onClose: () => void;
    createdAt: Date;
    tripId?: number;
    theme?: ToastTheme;
}

function timeAgo(date: Date): string {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return `${diff} sec${diff !== 1 ? "s" : ""} ago`;
    if (diff < 3600) {
        const mins = Math.floor(diff / 60);
        return `${mins} min${mins !== 1 ? "s" : ""} ago`;
    }
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
    title,
    message,
    variant = "info",
    onClose,
    createdAt,
    theme,
}) => {
    const resolvedTheme = theme ?? resolveToastThemeFromDom();
    const normalizedVariant = normalizeToastVariant(variant);
    const colors = getToastColors(normalizedVariant, resolvedTheme);
    const now = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const ago = useMemo(() => timeAgo(createdAt), [createdAt, now]);

    return (
        <div
            data-testid="notification-toast-root"
            data-toast-theme={resolvedTheme}
            data-toast-variant={normalizedVariant}
            style={{
                borderRadius: 8,
                boxShadow: colors.shadow,
                minWidth: 350,
                maxWidth: 450,
                overflow: "hidden",
                marginBottom: 12,
                backgroundColor: colors.messageBackground,
            }}
        >
            <div
                data-testid="notification-toast-header"
                style={{
                    backgroundColor: colors.headerBackground,
                    color: colors.headerText,
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px 6px 16px",
                    fontSize: 15,
                    fontWeight: 600,
                }}
            >
                <div style={{ flex: 1 }}>{title}</div>
                <div
                    style={{
                        fontSize: 12,
                        color: colors.metaText,
                        marginRight: 12,
                        fontWeight: 400,
                    }}
                >
                    {ago}
                </div>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                    }}
                    style={{
                        background: "none",
                        border: "none",
                        color: colors.metaText,
                        fontSize: 18,
                        cursor: "pointer",
                        marginLeft: 4,
                        lineHeight: 1,
                        padding: 0,
                    }}
                    aria-label="Close"
                    type="button"
                >
                    ×
                </button>
            </div>
            <div
                data-testid="notification-toast-message"
                style={{
                    backgroundColor: colors.messageBackground,
                    color: colors.messageText,
                    fontSize: 15,
                    padding: "14px 18px 14px 18px",
                    fontWeight: 600,
                }}
            >
                {message}
            </div>
        </div>
    );
};

export default NotificationToast;
